import { RefCallback, useCallback, useRef } from 'react';
import { useUnmount } from 'react-use';

import {
  UseIntersectionObserverOptions,
  UseIntersectionObserverResult,
} from './useIntersectionObserver.types';

/**
 * Represents a hook that uses the intersection observer to register
 * intersections with the viewport or a container element.
 *
 * @example
 *   const { observeElementRef } = useIntersectionObserver({
 *     handleIntersection: (entry) => {},
 *   });
 *
 *   <div ref={observeElementRef} />;
 */
export const useIntersectionObserver = ({
  options,
  onIntersection,
}: UseIntersectionObserverOptions): UseIntersectionObserverResult => {
  const handleIntersection = useCallback<IntersectionObserverCallback>(
    (entries) => entries.forEach(onIntersection),
    [onIntersection],
  );

  const intersectionObserver = useRef(
    new IntersectionObserver(handleIntersection, options),
  );

  useUnmount(() => intersectionObserver.current.disconnect());

  const handleObserveElementRef = useCallback<RefCallback<HTMLElement>>(
    (element) => {
      if (element) {
        intersectionObserver.current.observe(element);
      }
    },
    [],
  );

  const handleObserveElement = useCallback((element: HTMLElement) => {
    intersectionObserver.current.observe(element);
  }, []);

  return {
    observeElementRef: handleObserveElementRef,
    observeElement: handleObserveElement,
  };
};
